<template>
	<div class="grow overflow-y-auto">
		<SupportChatConversationList
			v-if="showHistory"
			class="flex-1 min-h-0"
			:conversations="conversationList"
			@new-chat="onNewChat"
			@load-conversation="onLoadConversation"
			@delete-conversation="onDeleteConversation"
			@rename-conversation="onRenameConversation"
		/>
		<template v-else>
			<SupportChat
				v-if="!showHistory"
				:chat-history="chatHistory"
				:validating="validating"
				:chat-context="chatContext"
				:status-message="statusMessage"
				:show-input="!ticketToolCall"
				:is-open="isOpen"
				show-feedback
				allow-file-upload
				allow-abort-request
				@submit-prompt="onSubmitPrompt"
				@abort-request="onAbortRequest"
				@upload-files="onUploadFiles"
				@add-form-context="onAddFormContext"
				@remove-context="onRemoveContext"
				@submit-feedback="onSubmitFeedback"
			>
				<template #above-input>
					<div v-if="builderAgentUtils.builderAgentEnabled.value" class="text-center text-warning bg-warning border border-warning p-2 sz-2">
						Form builder agent active
					</div>
				</template>
				<template #bottom>
					<SupportChatTicketForm
						v-if="ticketToolCall"
						:tool-call="ticketToolCall"
						:global-store="globalStore"
						@submit="onCreateTicket"
						@close="onCloseTicket"
					/>
				</template>
			</SupportChat>
		</template>
	</div>
</template>

<script lang="ts">
	import { computed, defineComponent, inject, onMounted, ref, watch } from 'vue';
	import SupportChat, { type UIChatContext } from './SupportChat.vue';
	import SupportChatConversationList from './SupportChatConversationList.vue';
	import SupportChatTicketForm from './SupportChatTicketForm.vue';
	import notify from 'src/utilities/notify';
	import { promptSupportChat, streamSupportChatResponse, createTicket, completeToolCall, generateSummary } from '@cognitoforms/api/services/support-chat-service';
	import { getAllConversations, getConversation, deleteConversation, renameConversation, submitFeedback } from '@cognitoforms/api/services/conversation-service';
	import { globalStore } from 'src/stores/global-store';
	import { uploadFile } from '@cognitoforms/api/services/file-service';
	import { Deferred } from 'src/utilities/deferred';
	import { useRoute } from 'vue-router/composables';
	import { SaveFormEvent, useEventBus } from '../../views/admin/utils/useEventBus';
	import { ConversationType } from '@cognitoforms/types/server-types/model/conversation-type';
	import { LLMResponseType } from '@cognitoforms/types/server-types/core/services/forms/a-i/llm-response-type';
	import { ChatContextType } from '@cognitoforms/types/server-types/model/chat-context-type';
	import { AppPage } from '@cognitoforms/types/server-types/model/app-page';
	import type { Conversation } from '@cognitoforms/types/server-types/model/conversation';
	import type { ChatHistoryViewModel } from '@cognitoforms/types/server-types/core/services/forms/a-i/chat-history-view-model';
	import type { FileDataRef } from '@cognitoforms/types/server-types/model/file-data-ref';
	import type { LLMResponse } from '@cognitoforms/types/server-types/core/services/forms/a-i/llm-response';
	import type { LLMToolCall } from '@cognitoforms/types/server-types/core/services/forms//a-i/llm-tool-call';
	import type { LlmPageMeta } from '@cognitoforms/types/server-types/core/services/forms/a-i/llm-page-meta';
	import type { CreateTicketArgs } from '@cognitoforms/api/services/support-chat-service';
	import type { SupportChatStatusMessage } from './SupportChatMessages.vue';
	import { builderAgentComposableKey } from 'src/utilities/injection-keys';
	import { AdvancedFormGenerator } from '@cognitoforms/form-generator';
	import type { Form } from '@cognitoforms/types/server-types/core/model/forms/definition/form';

	export default defineComponent({
		components: {
			SupportChat,
			SupportChatConversationList,
			SupportChatTicketForm
		},
		props: {
			showHistory: { type: Boolean, default: false },
			isOpen: { type: Boolean, default: true }
		},
		emits: ['show-history', 'update-chat-header', 'has-history', 'is-thinking'],
		setup(props, { emit }) {
			const route = useRoute();
			const chatHistory = ref<ChatHistoryViewModel[]>([]);
			const chatContext = ref<UIChatContext[]>([]);
			const validating = ref(false);
			const conversationId = ref<string>(crypto.randomUUID());
			const conversationList = ref<Conversation[]>([]);
			let eventSource: EventSource | null = null;
			let fileUploadsReady = new Deferred<void>();
			fileUploadsReady.resolve();

			onMounted(async () => {
				const hasHistory = (await getAllConversations(ConversationType.SupportAgent, 1)).length > 0;
				emit('has-history', hasHistory);
			});

			const pageMeta = computed<LlmPageMeta>(() => {
				const getFormId = () => (
					Object.values(globalStore.forms).find(f => f.UrlName === route.params.formUrlName.toLocaleLowerCase())?.Id
				);

				// There's quite a few settings routes, so we check the path here
				if (route.fullPath.startsWith('/settings'))
					return { Page: AppPage.Settings };

				switch (route.name) {
				case 'myaccount':
				case 'account-settings':
					return { Page: AppPage.Account };
				case 'builder-new':
					return { Page: AppPage.Build, FormId: 'new' };
				case 'builder':
					return { Page: AppPage.Build, FormId: getFormId() };
				case 'entries':
					return { Page: AppPage.Entries, FormId: getFormId() };
				case 'home':
				case 'forms':
					return { Page: AppPage.Home };
				case 'publish':
					return { Page: AppPage.Publish, FormId: getFormId() };
				case 'task-dashboard':
					return { Page: AppPage.Tasks };
				default:
					return { Page: AppPage.Home };
				}
			});

			const builderAgentUtils = inject(builderAgentComposableKey)!;
			let formGenerator: AdvancedFormGenerator | undefined;
			watch(builderAgentUtils.formStore, () => {
				if (!builderAgentUtils.builderService.value)
					return;

				formGenerator = new AdvancedFormGenerator({ builderService: builderAgentUtils.builderService.value, formStore: builderAgentUtils.formStore.value });
				formGenerator.setState(builderAgentUtils.builderService.value.form as Form);
			}, { immediate: true });

			const ticketToolCall = ref<LLMToolCall | null>(null);
			const statusMessage = ref<SupportChatStatusMessage>('Thinking');
			const onSubmitPrompt = async (prompt: string, isFeedback: boolean = false) => {
				statusMessage.value = 'Thinking';

				// Wait for all files to finish uploading
				await fileUploadsReady.promise;

				validating.value = true;

				// Add the prompt to chat history
				const chatItem: ChatHistoryViewModel = {
					Id: crypto.randomUUID(),
					Prompt: prompt,
					Response: '',
					IsHidden: isFeedback,
					Context: chatContext.value,
					TicketCreated: false,
					Feedback: null
				};
				chatHistory.value.push(chatItem);

				// Update chat header if this is the first message
				if (chatHistory.value.length === 1)
					emit('update-chat-header', prompt, conversationId.value, false);

				try {
					// Update has-history status
					emit('has-history', true);

					if (builderAgentUtils.builderAgentEnabled.value) {
						const result = await formGenerator.refineForm(prompt);
						chatItem.Response = result?.userMessage || '';
						validating.value = false;
					}
					else {
						// Submit the prompt
						const promptPromise = promptSupportChat(conversationId.value, prompt, chatContext.value, pageMeta.value.Page);

						// Clear files from prompt context after submission
						chatContext.value = chatContext.value.filter(item => item.Type !== ChatContextType.File);

						// Mark all transient context as non-transient
						chatContext.value.forEach(item => (item.transient = false));

						// Start streaming the response after prompt is submitted
						await promptPromise;

						eventSource = streamSupportChatResponse(
							conversationId.value,
							pageMeta.value,
							async (response: LLMResponse) => {
								if (response.Type === LLMResponseType.Response && response.Response) {
									chatItem.Response += response.Response;
								}
								else if (response.Type === LLMResponseType.ToolCall && response.ToolCall) {
									if (response.ToolCall.ToolName === 'ShowTicketInterface') {
										ticketToolCall.value = response.ToolCall;
									}
									else if (response.ToolCall.ToolName === 'GetForm') {
										statusMessage.value = 'Analyzing form';
										if (pageMeta.value.Page === AppPage.Build) {
											const form = window.Cognito.Forms.model.currentForm;
											const serializedForm = JSON.stringify(window.Cognito.serialize(form));
											completeToolCall(conversationId.value, response.ToolCall, serializedForm);
										}
										else {
											completeToolCall(conversationId.value, response.ToolCall, null);
										}
									}
									else if (response.ToolCall.ToolName === 'SearchDocumentation' || response.ToolCall.ToolName === 'GetSupportArticle') {
										statusMessage.value = 'Searching documentation';
									}
									else if (response.ToolCall.ToolName === 'SearchTemplates') {
										statusMessage.value = 'Searching templates';
									}
								}
							},
							(error) => {
								console.error('Error streaming response:', error);
								if (!chatItem.Response) {
									chatItem.Response = 'An error occurred while streaming the response.';
								}
								validating.value = false;
								eventSource = null;
							},
							async () => {
								validating.value = false;
								eventSource = null;

								// Generate summary for the conversation if this is the first response
								let hasPreviousResponse = false;
								for (const item of chatHistory.value) {
									if (item === chatItem) {
										break;
									}
									if (item.Response && item.Response.trim() !== '') {
										hasPreviousResponse = true;
										break;
									}
								}

								if (!hasPreviousResponse) {
									try {
										// Conversation ID can change during summary generation, so we store it now
										const currentConversationId = conversationId.value;
										const summary = await generateSummary(currentConversationId, chatItem.Prompt, chatItem.Response);
										emit('update-chat-header', summary, currentConversationId, true);
									}
									catch (error) {
										console.error('Failed to generate summary:', error);
									}
								}
							}
						);
					}
				}
				catch (error) {
					console.error('Error submitting prompt:', error);
					chatItem.Response = 'An error occurred. Please try again.';
					validating.value = false;
				}
			};

			const onAbortRequest = (reset: boolean) => {
				if (eventSource) {
					eventSource.close();
					eventSource = null;
					chatHistory.value[chatHistory.value.length - 1].Response = 'Request Cancelled.';
				}

				validating.value = false;
				if (!reset)
					return;

				ticketToolCall.value = null;
				chatHistory.value = [];
				chatContext.value = [];
				conversationId.value = crypto.randomUUID();
			};

			const onResetConversation = () => {
				if (eventSource) {
					eventSource.close();
					eventSource = null;
				}

				emit('update-chat-header', '', conversationId.value);
				chatHistory.value = [];
				chatContext.value = [];
				conversationId.value = crypto.randomUUID();
				validating.value = false;
				ticketToolCall.value = null;
			};

			const addCurrentFormToContext = (transient: boolean) => {
				if (pageMeta.value.Page !== AppPage.Build)
					return;

				const formName = (pageMeta.value.FormId === 'new') ? 'Current Form' : `${globalStore.forms[pageMeta.value.FormId].Name} (Current Form)`;

				// Add at the start of the context list
				chatContext.value.unshift({
					id: pageMeta.value.FormId,
					name: formName,
					transient,
					EntityId: pageMeta.value.FormId,
					Type: ChatContextType.Form
				});
			};

			// Called by parent component and conversation list to clear the conversation
			const onNewChat = () => {
				emit('show-history', false);
				onResetConversation();
				addCurrentFormToContext(true); // transient: true
			};

			// Conversation History
			const loadConversations = async () => {
				try {
					conversationList.value = await getAllConversations(ConversationType.SupportAgent);
				}
				catch (error) {
					console.error('Failed to load all support conversations:', error);
				}
			};

			// Load history when showHistory prop changes to true
			watch(() => props.showHistory, async (isShown) => {
				if (isShown)
					await loadConversations();
			}, { immediate: true });

			watch(validating, () => {
				emit('is-thinking', validating.value);
			});

			const onLoadConversation = async (id: string, summary: string) => {
				onResetConversation();
				emit('show-history', false);

				try {
					const chatData = await getConversation(ConversationType.SupportAgent, id);
					const conversationContext = conversationList.value.find(c => c.ConversationId === id)?.Context ?? [];

					chatHistory.value = chatData;
					conversationId.value = id;
					chatContext.value = conversationContext
						// Filter out the current form (we'll add it back in separately) or any forms that no longer exist
						.filter(ctx => globalStore.forms[ctx.EntityId] && ctx.EntityId !== pageMeta.value.FormId)
						.map(ctx => ({
							...ctx,
							id: ctx.EntityId,
							name: globalStore.forms[ctx.EntityId].Name,
							transient: false

						})) || [];

					// Mark as transient if the current form was not already in the supplied context
					const transient = !conversationContext.some(ctx => ctx.EntityId === pageMeta.value.FormId);
					addCurrentFormToContext(transient);
					emit('update-chat-header', summary, id);
				}
				catch (error) {
					console.error('Failed to load conversation:', error);
				}
			};

			const onDeleteConversation = async (id: string) => {
				conversationList.value = conversationList.value.filter(c => c.ConversationId !== id);
				await deleteConversation(ConversationType.SupportAgent, id);

				// If the deleted conversation is currently loaded, reset the chat
				if (id === conversationId.value)
					onResetConversation();

				// Update has-history status if last conversation was deleted
				if (conversationList.value.length === 0) {
					emit('has-history', false);
					emit('show-history', false);
				}
			};

			const onRenameConversation = async (id: string, newName: string) => {
				const conversation = conversationList.value.find(c => c.ConversationId === id);
				if (conversation) {
					conversation.Summary = newName;
					await renameConversation(ConversationType.SupportAgent, id, newName);
				}
			};

			const onAddFormContext = (formId: string) => {
				if (!globalStore.forms[formId])
					return;

				chatContext.value.push({
					id: formId,
					name: globalStore.forms[formId].Name,
					transient: false,
					EntityId: formId,
					Type: ChatContextType.Form
				});
			};

			watch(pageMeta, (newMeta, oldMeta) => {
				// Remove transient context and unsaved 'new' form context
				chatContext.value = chatContext.value.filter(ctx => ctx.transient !== true || (ctx.EntityId === 'new' && ctx.Type === ChatContextType.Form));

				if (oldMeta && oldMeta.Page === AppPage.Build) {
					if (oldMeta.FormId === 'new') {
						// Remove unsaved "new" form context
						const oldFormContext = chatContext.value.findIndex(ctx => ctx.Type === ChatContextType.Form && ctx.EntityId === 'new');
						if (oldFormContext !== -1)
							chatContext.value.splice(oldFormContext, 1);
					}
					else {
						// Remove "(Current Form)" from name of the form that was navigated away from
						const oldFormContext = chatContext.value.find(ctx => ctx.EntityId === oldMeta.FormId);
						if (oldFormContext)
							oldFormContext.name = globalStore.forms[oldFormContext.EntityId].Name;
					}
				}

				// Add current form context if on builder page
				if (newMeta.Page === AppPage.Build) {
					let removedFromList = false;
					chatContext.value = chatContext.value.filter(ctx => {
						if (ctx.EntityId === newMeta.FormId) {
							removedFromList = true;
							return false;
						}
						return true;
					});

					const transient = !removedFromList;
					addCurrentFormToContext(transient);
				}
			}, { immediate: true });

			// Handle when a new form is saved for the first time; update form name
			const { on: onSaveFormEvent } = useEventBus(SaveFormEvent);
			onSaveFormEvent(({ formId, formName }) => {
				const currentFormCtx = chatContext.value.find(ctx => ctx.Type === ChatContextType.Form && ctx.EntityId === 'new');
				if (!currentFormCtx)
					return;

				currentFormCtx.id = formId;
				currentFormCtx.name = `${formName} (Current Form)`;
				currentFormCtx.EntityId = formId;
			});

			// File upload handlers
			const appendNumberToFileName = (name: string, i: number) => (
				`${name.split('.').slice(0, -1).join('.')} (${i}).${name.split('.').pop()}`
			);

			const getUniqueFileName = (fileName: string, i = 1): string => {
				const newName = (i === 1) ? fileName : appendNumberToFileName(fileName, i);
				if (chatContext.value.find(item => item.name === newName))
					return getUniqueFileName(fileName, i + 1);
				else
					return newName;
			};

			const onUploadFiles = async (files: File[]) => {
				fileUploadsReady = new Deferred<void>();

				const filesWithNames = files.map(file => {
					if (file.size > 10_000_000) { // 10MB
						notify('Only files smaller than 10 MB may be uploaded.', 'error');
						return null;
					}

					return {
						file,
						assignedName: getUniqueFileName(file.name)
					};
				});

				// Add placeholder items
				for (const { assignedName } of filesWithNames) {
					chatContext.value.push({
						id: crypto.randomUUID(),
						name: assignedName,
						transient: false,
						Type: ChatContextType.File
					} as UIChatContext);
				}

				// Upload files
				for (const { assignedName, file } of filesWithNames) {
					try {
						const context = chatContext.value.find(i => i.name === assignedName && i.Type === ChatContextType.File);
						if (!context)
							continue;

						const uploadOptions = {
							encrypt: false,
							onProgress: () => {}
						};

						const fileDataRef = await uploadFile(file, uploadOptions) as FileDataRef;
						context.EntityId = fileDataRef.Id;
					}
					catch (e) {
						// Remove the context item on failure
						chatContext.value = chatContext.value.filter(i => i.name !== assignedName);
					}
				}

				// Release the submit prompt lock
				fileUploadsReady.resolve();
			};

			const onRemoveContext = (itemId: string) => {
				chatContext.value = chatContext.value.filter(item => item.id !== itemId);
			};

			const handleFormUpdate = (context: UIChatContext) => {
				const form = globalStore.forms[context.EntityId];
				if (form)
					context.name = form.Name;
				else
					onRemoveContext(context.id);
			};

			const formWatchers = ref<Record<string, () => void>>({});
			watch(chatContext, (newContext, oldContext) => {
				const added = newContext.filter(nc => !oldContext.find(oc => oc.id === nc.id));
				const removed = oldContext.filter(oc => !newContext.find(nc => nc.id === oc.id));

				// Set up watchers for added form contexts
				for (const ctx of added) {
					if (ctx.Type === ChatContextType.Form && ctx.EntityId !== 'new' && !formWatchers.value[ctx.EntityId])
						formWatchers.value[ctx.EntityId] = watch(() => globalStore.forms[ctx.EntityId], () => handleFormUpdate(ctx), { deep: true });
				}

				// Remove watchers for removed form contexts
				for (const ctx of removed) {
					if (ctx.Type === ChatContextType.Form && formWatchers.value[ctx.EntityId]) {
						formWatchers.value[ctx.EntityId]();
						delete formWatchers.value[ctx.EntityId];
					}
				}
			});

			const onCreateTicket = async (e: CreateTicketArgs) => {
				try {
					await createTicket(conversationId.value, e);
					chatHistory.value.at(-1).TicketCreated = true;
				}
				catch {
					// Handle error if needed
				}

				setTimeout(() => {
					ticketToolCall.value = null;
				}, 5000);
			};

			const onCloseTicket = () => {
				ticketToolCall.value = null;
			};

			const onSubmitFeedback = async (feedback: boolean | null) => {
				try {
					const feedbackResponse = await submitFeedback(
						ConversationType.SupportAgent,
						conversationId.value,
						feedback
					);

					if (feedback === false && feedbackResponse?.Response != null) {
						chatHistory.value.push(feedbackResponse);
					}
				}
				catch (error) {
					console.error('Failed to submit feedback:', error);
				}
			};

			return {
				chatHistory,
				chatContext,
				validating,
				conversationList,
				ticketToolCall,
				globalStore,
				statusMessage,
				builderAgentUtils,
				onSubmitPrompt,
				onResetConversation,
				onAbortRequest,
				onLoadConversation,
				onNewChat,
				onDeleteConversation,
				onAddFormContext,
				onRenameConversation,
				onUploadFiles,
				onRemoveContext,
				onCreateTicket,
				onCloseTicket,
				onSubmitFeedback
			};
		}
	});
</script>
